import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { isArray, isPlainObject, mapValues } from 'lodash'
import dayjs from 'dayjs'

import { TOOL_TABS } from '../Constants'
import { Feature } from '../Features'
import * as features from '../Features'
import { renderUIField, translateFeature } from '../helpers/FeatureProcessor'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import {
  resetValidation,
  setInvalidGroups,
  setValidation,
  setValidationError,
} from 'redux/slices/ToolBuilder'
interface IdeaProps {
  tab: string
}

const ideaFeatures: any[] = [
  {
    key: 'title',
    tab: TOOL_TABS.FIRST,
    props: {
      required: true,
      inputProps: { maxLength: 150 },
    },
  },
  {
    key: 'description',
    tab: TOOL_TABS.FIRST,
    props: {
      inputProps: { maxLength: 400 },
    },
  },
  {
    key: 'archivalMessage',
    tab: TOOL_TABS.FIRST,
    props: {},
  },
  {
    key: 'allowUnverifiedParticipation',
    tab: TOOL_TABS.SECOND,
    props: {},
  },
  {
    key: 'allowComments',
    tab: TOOL_TABS.SECOND,

    props: {},
  },
  {
    key: 'allowImageUpload',
    tab: TOOL_TABS.SECOND,

    props: {},
  },
  {
    key: 'allowReactions',
    tab: TOOL_TABS.SECOND,

    props: {},
  },
  {
    key: 'allowNotification',
    tab: TOOL_TABS.SECOND,

    props: {
      adminEmails: {},
    },
  },
]
const validationRules = [
  {
    label: 'toolBuilder.features.title',
    tab: TOOL_TABS.FIRST,
    id: 'title',
    rules: [{ required: true }, { maxLength: 150 }],
  },
  {
    label: 'toolBuilder.features.permalink',
    tab: TOOL_TABS.FIRST,
    id: 'permaLink',
    rules: [{ required: true }, { minLength: 3 }],
  },
  {
    id: 'limitVotingCount',
    label: 'toolBuilder.features.reaction.limitVoting.count',
    tab: TOOL_TABS.SECOND,
    rules: [
      { required: true, when: ['limitVoting', 'allowReactions'] },
      { minValue: 1, when: ['limitVoting', 'allowReactions'] },
      { maxValue: 10, when: ['limitVoting', 'allowReactions'] },
    ],
  },
  {
    id: 'scheduledReactionsDateRange',
    label: 'toolBuilder.features.reaction.scheduled.title',
    tab: TOOL_TABS.SECOND,
    rules: [
      { required: true, when: ['allowScheduleReactions', 'allowReactions'] },
      {
        startAndEndDate: 2,
        when: ['allowScheduleReactions', 'allowReactions'],
      },
    ],
  },

  {
    label: 'toolBuilder.features.notification.emails',
    tab: TOOL_TABS.SECOND,
    id: 'adminEmails',
    rules: [{ required: true, when: ['notifyAdmins'] }],
  },
]
const idLabelMapping: any = {}

const findIdLable = (obj: any) => {
  if (obj?.id && obj?.label) {
    idLabelMapping[obj.id] = obj.label
  }
  mapValues(obj, (value) => {
    if (isPlainObject(value)) {
      findIdLable(value)
    }
    if (isArray(value)) {
      value.map((item) => findIdLable(item))
    }
  })
}
Object.values(features).forEach((feature) => {
  findIdLable(feature)
})

const Idea = forwardRef(({ tab }: IdeaProps, ref) => {
  const { t } = useTranslation()
  const toolState = useAppSelector((state) => state.toolBuilder.toolBuilder)
  const validation = useAppSelector((state) => state.toolBuilder.validation)
  const dispatch = useAppDispatch()

  const [displayIdeaFeatures, setDisplayIdeaFeatures] = useState<Feature[]>([])
  const [displayFeatures, setDisplayFeatures] = useState<any[]>([])

  useEffect(() => {
    setDisplayIdeaFeatures(
      ideaFeatures.filter((feature) => t(feature.tab) === tab),
    )
  }, [tab])

  useEffect(() => {
    const feats = displayIdeaFeatures.map((feature) => {
      const { key, props } = feature
      const foundFeature = Object.values(features).find((f) => {
        return f.id === key
      })

      return {
        ...foundFeature,
        props,
      }
    })
    setDisplayFeatures(feats)
  }, [displayIdeaFeatures])

  const validate = () => {
    let isValid = true
    dispatch(resetValidation())

    validationRules.forEach((item) => {
      const { label, tab, id, rules } = item
      const translatedTab = t(tab)
      const value = toolState[id]

      rules.forEach((rule) => {
        const {
          required,
          minLength,
          maxLength,
          when,
          startAndEndDate,
          minValue,
          maxValue,
        } = rule as any

        if (
          minValue &&
          ((when && when.every((field: string) => toolState[field])) ||
            !when) &&
          value < minValue
        ) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.minValue', {
                field: t(label),
                minValue: minValue,
              }),
            }),
          )
          isValid = false
        }
        if (
          maxValue &&
          ((when && when.every((field: string) => toolState[field])) ||
            !when) &&
          value > maxValue
        ) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.maxValue', {
                field: t(label),
                maxValue: maxValue,
              }),
            }),
          )
          isValid = false
        }

        if (
          required &&
          ((when && when.every((field: string) => toolState[field])) ||
            !when) &&
          (!value ||
            (value &&
              ((Array.isArray(value) && value.length < 1) ||
                (typeof value === 'string' && value?.trim()?.length < 1))))
        ) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.required', {
                field: t(label),
              }),
            }),
          )
          isValid = false
        }
        if (
          minLength &&
          ((when && when.every((field: string) => toolState[field])) ||
            !when) &&
          value?.length < minLength
        ) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.minLength', {
                field: t(label),
                minLength: minLength,
              }),
            }),
          )
          isValid = false
        }

        if (
          startAndEndDate &&
          ((when && when.every((field: string) => toolState[field])) ||
            !when) &&
          (value?.length < 2 ||
            !value?.[0] ||
            !value?.[1] ||
            !dayjs(value?.[0]).isValid() ||
            !dayjs(value?.[1]).isValid())
        ) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.startAndEndDate'),
            }),
          )
          isValid = false
        }
        if (maxLength && value?.length > maxLength) {
          dispatch(setInvalidGroups({ name: translatedTab }))
          dispatch(
            setValidation({
              name: id,
              value: t('toolBuilder.validation.maxLength', {
                field: t(label),
                maxLength: maxLength,
              }),
            }),
          )
          isValid = false
        }
      })
    })
    if (!isValid) {
      dispatch(
        setValidationError({ error: t('toolBuilder.fillAllMandatoryFields') }),
      )
    }

    return isValid
  }

  useImperativeHandle(ref, () => ({
    isValid: () => validate(),
  }))

  return (
    <>
      {displayFeatures.map((feature: Feature, index: number) => {
        const { props, ...rest } = translateFeature(feature, t)
        const { id } = feature
        const uiField = renderUIField(rest, {
          ...props,
          error: !!validation[id],
          helperText: validation[id] || '',
        })

        return <Box key={index}>{uiField}</Box>
      })}
    </>
  )
})

Idea.displayName = 'IdeaTemplate'

export { Idea, ideaFeatures }
